// hooks/useWindowOperations.js
import { useState, useRef, useEffect } from 'react';
import { usePanelContext } from '../components/contexts/PanelContext';

const MIN_SIZE = { width: 50, height: 50 };

export function useWindowOperations({ sticky, updateSticky, onFocus, isInMainPanel = false }) {
  const { isSwapped } = usePanelContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(Boolean(sticky.is_minimized));
  
  // Determine which coordinate system to use
  // Use main coordinates when:
// 1. We're in main panel and ARE swapped, or
// 2. We're in right panel and are NOT swapped
const useMainCoords = isInMainPanel;

// console.log('🎯 Coordinate system:', { 
//   isInMainPanel, 
//   isSwapped, 
//   useMainCoords,
//   coords: useMainCoords ? 'main_x/y' : 'x/y'
// });
  
  useEffect(() => {
    setIsMinimized(Boolean(sticky.is_minimized));
  }, [sticky.is_minimized]);
 
  const dragRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeRef = useRef(null);
  const initialSize = useRef(null);
  const originalDimensions = useRef(null);

  // Helper to get current coordinates based on panel state
  const getCurrentCoords = () => {
    if (useMainCoords) {
      return {
        x: sticky.main_x ?? 0,
        y: sticky.main_y ?? 0,
        width: sticky.main_width ?? sticky.width ?? MIN_SIZE.width,
        height: sticky.main_height ?? sticky.height ?? MIN_SIZE.height
      };
    } else {
      return {
        x: sticky.x ?? 0,
        y: sticky.y ?? 0,
        width: sticky.width ?? MIN_SIZE.width,
        height: sticky.height ?? MIN_SIZE.height
      };
    }
  };

  // Helper to update coordinates based on panel state
  const updateCoords = (updates) => {
    return useMainCoords ? {
      main_x: updates.x,
      main_y: updates.y,
      main_width: updates.width,
      main_height: updates.height
    } : updates;
  };

  const toggleMinimize = () => {
    const newMinimizedState = !isMinimized;
    setIsMinimized(newMinimizedState);
    onFocus?.();
    updateSticky(sticky.id, {
      is_minimized: newMinimizedState
    });
  };

  const toggleExpand = () => {
    onFocus?.();
    
    // Find container based on current mode
    const container = document.querySelector(useMainCoords ? 
      '.content-container' : 
      '[class*="rightPanel"]'
    );
    if (!container) return;

    if (!isExpanded) {
      const coords = getCurrentCoords();
      originalDimensions.current = { ...coords };

      const padding = 20;
      const rightMargin = 40; // Extra margin for right side
      const { width, height } = container.getBoundingClientRect();
      const newWidth = width - (padding + rightMargin); // More space on right
      const newHeight = height - (padding * 2);

      updateSticky(sticky.id, updateCoords({
        x: padding,
        y: padding + 80,
        width: newWidth,
        height: newHeight * 0.72
      }));
    } else if (originalDimensions.current) {
      updateSticky(sticky.id, updateCoords(originalDimensions.current));
    }
    setIsExpanded(!isExpanded);
  };

  const startDrag = (e) => {
    ///console.log('🎯 startDrag', { isInMainPanel, isSwapped, useMainCoords });
    
    // First try closest parent
    let container = e.target.closest('.content-container, [class*="rightPanel"]');
    ///console.log('🎯 Found container:', container?.className);
    
    // Fallback to query selector if needed
    if (!container) {
      container = document.querySelector(useMainCoords ? 
        '.content-container' : 
        '[class*="rightPanel"]'
      );
    }
    
    if (!container) {
      console.log('❌ No container found');
      return;
    }

    const coords = getCurrentCoords();
    ///console.log('🎯 Starting drag with coords:', coords);
    
    dragRef.current = { container };
    dragOffset.current = {
      x: e.clientX - coords.x,
      y: e.clientY - coords.y
    };
    
    onFocus?.();
    document.body.classList.add('dragging');
    
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', endDrag);
  };
  
const handleDrag = (e) => {
  if (!dragRef.current?.container) {
    console.log('❌ No container in dragRef');
    return;
  }
  
  const container = dragRef.current.container;
  const coords = getCurrentCoords();
  
  // Get actual list panel height (it's resizable)
  const listPanel = document.querySelector('.stickies-list-panel-main');
  const listPanelHeight = listPanel ? listPanel.offsetHeight : 30;
  
  // Small gaps to match NotePreviewSticky
  const LEFT_GAP = 1;
  const TOP_GAP = 1;
  const BOTTOM_GAP = 5;
  
  if (isMinimized) {
    updateSticky(sticky.id, updateCoords({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
      width: coords.width,
      height: coords.height
    }));
    return;
  }

  // Calculate max boundaries with gaps
  const maxX = container.clientWidth - coords.width;
  const maxY = container.clientHeight - listPanelHeight - coords.height - BOTTOM_GAP;
  
  // Constrain position with gaps
  const newX = Math.max(LEFT_GAP, Math.min(e.clientX - dragOffset.current.x, maxX));
  const newY = Math.max(TOP_GAP, Math.min(e.clientY - dragOffset.current.y, maxY));

  updateSticky(sticky.id, updateCoords({
    x: newX,
    y: newY,
    width: coords.width,
    height: coords.height
  }));
};

  const endDrag = () => {
    dragRef.current = null;
    document.body.classList.remove('dragging');
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', endDrag);
  };

  const startResize = (e) => {
    console.log('📏 startResize');
    e.preventDefault();
    e.stopPropagation();
    onFocus?.();
    
    document.body.classList.add('resizing-sticky');
    resizeRef.current = { x: e.clientX, y: e.clientY };
    
    const coords = getCurrentCoords();
    initialSize.current = { 
      width: coords.width, 
      height: coords.height 
    };
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', endResize);
  };

  const handleResize = (e) => {
    if (!resizeRef.current) return;
    
    const deltaX = e.clientX - resizeRef.current.x;
    const deltaY = e.clientY - resizeRef.current.y;
    
    const coords = getCurrentCoords();
    const isEntryMode = sticky.type === 'ai' && (!sticky.content || sticky.content === 'AI Sticky Note');
    
    const newWidth = Math.max(MIN_SIZE.width, initialSize.current.width + deltaX);
    const newHeight = isEntryMode 
      ? initialSize.current.height
      : Math.max(MIN_SIZE.height, initialSize.current.height + deltaY);

    updateSticky(sticky.id, updateCoords({
      x: coords.x,
      y: coords.y,
      width: newWidth,
      height: newHeight
    }));
  };

  const endResize = () => {
    document.body.classList.remove('resizing-sticky');
    resizeRef.current = null;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', endResize);
  };

  return {
    isExpanded,
    isMinimized,
    startDrag,
    startResize,
    toggleMinimize,
    toggleExpand
  };
}